import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import api from '../api/client';
import { resolveImage } from '../components/admin/ImageUpload';
import { ChevronRight, SlidersHorizontal, Loader2, Tag, X } from 'lucide-react';
import SEO, { breadcrumbSchema } from '../components/SEO';

const isOnSale = (p) => (p.wasPrice && p.wasPrice > p.price)
  || (p.discount_end_user > 0) || (p.discount_builder > 0) || (p.discount_retailer > 0);

/**
 * Category / Brand / Sale product listing with advanced sidebar filters:
 *   - Price range (min + max)
 *   - Brand checkboxes
 *   - Availability (in-stock only)
 *   - Product attributes (dynamic — voltage, power source, etc.)
 * The `sale` slug is a virtual category — server-side filters to items with
 * a real discount or wasPrice > price.
 */
const CategoryPage = () => {
  const { slug: rawSlug } = useParams();
  const [cities, setCities] = useState([]);

  // Load the UK city list once — used to detect the "-in-<city>" URL suffix.
  useEffect(() => {
    api.get('/cities').then(r => setCities(r.data || [])).catch(() => {});
  }, []);

  // Detect the "-in-<city>" suffix. Strip it before we look up the category so
  // one CategoryPage handles both `/category/tools` and `/category/tools-in-london`.
  const cityMatch = useMemo(
    () => cities.find(c => rawSlug?.endsWith(`-in-${c.slug}`)),
    [cities, rawSlug]
  );
  const slug = cityMatch
    ? rawSlug.slice(0, rawSlug.length - `-in-${cityMatch.slug}`.length)
    : rawSlug;
  const isSale = slug === 'sale';

  const [category, setCategory] = useState(() => isSale
    ? { name: 'Sale', slug: 'sale', seo_intro: '' }
    : { name: slug, slug });
  const [allCategories, setAllCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('featured');

  // Filter state
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(2000);
  const [priceCap, setPriceCap] = useState(2000);       // dynamic upper bound
  const [selBrands, setSelBrands] = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [selAttrs, setSelAttrs] = useState({});         // { voltage: 'Set of values' }
  const [showFilters, setShowFilters] = useState(false); // mobile drawer

  useEffect(() => {
    // If the URL contains "-in-…" wait until the cities list has arrived so we
    // can split correctly. Otherwise we'd fetch with the raw slug and 404.
    if (rawSlug?.includes('-in-') && cities.length === 0) return;
    setLoading(true);
    setSelBrands([]);
    setSelAttrs({});
    setInStockOnly(false);
    const params = isSale ? { on_sale: true } : { category: slug };
    Promise.all([
      api.get('/products', { params }),
      api.get('/categories')
    ]).then(([p, c]) => {
      setAllCategories(c.data);
      if (!isSale) {
        const cat = c.data.find(x => x.slug === slug);
        if (cat) setCategory(cat);
      }
      const list = p.data || [];
      setProducts(list);
      const cap = Math.max(500, Math.ceil((list.reduce((m, x) => Math.max(m, x.price || 0), 0) + 50) / 50) * 50);
      setPriceCap(cap);
      setMinPrice(0);
      setMaxPrice(cap);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug, isSale, rawSlug, cities.length]);

  const subCategories = useMemo(
    () => isSale ? [] : allCategories.filter(c => c.parent_id === category.id),
    [allCategories, category, isSale]
  );
  const parentCategory = useMemo(
    () => !isSale && category.parent_id ? allCategories.find(c => c.id === category.parent_id) : null,
    [allCategories, category, isSale]
  );

  const availableBrands = useMemo(
    () => [...new Set(products.map(p => p.brand).filter(Boolean))].sort(),
    [products]
  );

  // Aggregate attributes across all products in this listing.
  //   { voltage: Set{'18V','24V'}, power_source: Set{'Battery','Corded'} }
  const availableAttrs = useMemo(() => {
    const map = {};
    for (const p of products) {
      const a = p.attributes || {};
      for (const [k, v] of Object.entries(a)) {
        if (!v) continue;
        (map[k] = map[k] || new Set()).add(String(v));
      }
    }
    // sort each set's values
    return Object.fromEntries(Object.entries(map).map(([k, s]) => [k, [...s].sort()]));
  }, [products]);

  const toggleBrand = (b) => setSelBrands(prev => prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b]);
  const toggleAttr  = (k, v) => setSelAttrs(prev => {
    const set = new Set(prev[k] || []);
    if (set.has(v)) set.delete(v); else set.add(v);
    const next = { ...prev, [k]: [...set] };
    if (next[k].length === 0) delete next[k];
    return next;
  });
  const clearAll = () => { setSelBrands([]); setSelAttrs({}); setInStockOnly(false); setMinPrice(0); setMaxPrice(priceCap); };

  const filtered = useMemo(() => {
    let list = products.filter(p => (p.price >= minPrice) && (p.price <= maxPrice));
    if (selBrands.length) list = list.filter(p => selBrands.includes(p.brand));
    if (inStockOnly) list = list.filter(p => (p.stock || 0) > 0);
    if (Object.keys(selAttrs).length) {
      list = list.filter(p => {
        const attrs = p.attributes || {};
        return Object.entries(selAttrs).every(([k, vals]) =>
          vals.some(v => String(attrs[k] || '').toLowerCase() === String(v).toLowerCase())
        );
      });
    }
    switch (sort) {
      case 'price-asc': list = [...list].sort((a,b) => a.price - b.price); break;
      case 'price-desc': list = [...list].sort((a,b) => b.price - a.price); break;
      case 'rating': list = [...list].sort((a,b) => b.rating - a.rating); break;
      default: break;
    }
    return list;
  }, [products, sort, minPrice, maxPrice, selBrands, inStockOnly, selAttrs]);

  const activeFilterCount = (selBrands.length ? 1 : 0)
    + (inStockOnly ? 1 : 0)
    + (minPrice > 0 || maxPrice < priceCap ? 1 : 0)
    + Object.keys(selAttrs).length;

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      <SEO
        title={cityMatch
          ? `${category.name} in ${cityMatch.name}`
          : (isSale ? 'Sale — Discounted tools & materials' : category.name)}
        description={cityMatch
          ? `Quality ${category.name} supplies delivered across ${cityMatch.name}. ${filtered.length} professional products with next-day dispatch to all ${cityMatch.name} postcodes.`
          : (isSale
            ? `Shop the sale at Dr Tools — trade-price cuts on tools, materials and site essentials. ${filtered.length} discounted products with next-day UK delivery.`
            : `Shop ${category.name} at Dr Tools — ${filtered.length} products with trade pricing, next-day UK delivery and open credit accounts for builders.`)}
        // City pages canonical back to the master category URL so Google merges signal.
        path={`/category/${category.slug}`}
        jsonLd={[breadcrumbSchema([
          { name: 'Home', path: '/' },
          ...(parentCategory ? [{ name: parentCategory.name, path: `/category/${parentCategory.slug}` }] : []),
          { name: isSale ? 'Sale' : category.name, path: `/category/${category.slug}` },
          ...(cityMatch ? [{ name: cityMatch.name, path: `/category/${category.slug}-in-${cityMatch.slug}` }] : []),
        ])]}
      />
      <nav className="flex items-center gap-2 text-xs text-neutral-600 mb-4 flex-wrap">
        <Link to="/" className="hover:text-[#F46A25]">Home</Link>
        <ChevronRight className="w-3 h-3" />
        {parentCategory && (
          <>
            <Link to={`/category/${parentCategory.slug}`} className="hover:text-[#F46A25]">{parentCategory.name}</Link>
            <ChevronRight className="w-3 h-3" />
          </>
        )}
        {cityMatch ? (
          <>
            <Link to={`/category/${category.slug}`} className="hover:text-[#F46A25]">{category.name}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#12263A] font-semibold">{cityMatch.name}</span>
          </>
        ) : (
          <span className="text-[#12263A] font-semibold">{isSale ? 'Sale' : category.name}</span>
        )}
      </nav>

      <h1 className="text-3xl md:text-4xl font-black text-[#12263A] mb-2 flex items-center gap-2">
        {isSale && <Tag className="w-8 h-8 text-red-600" />}
        {cityMatch
          ? `Quality ${category.name} Supplies in ${cityMatch.name}`
          : (isSale ? 'Sale' : category.name)}
      </h1>
      <p className="text-sm text-neutral-600 mb-4">{filtered.length} products found{cityMatch ? ` — delivered across ${cityMatch.name}` : ''}</p>

      {/* Local-SEO intro (~100 words). Auto-generated per category × city so
          new categories inherit the treatment without any admin work. */}
      {cityMatch && (
        <div
          className="mb-6 bg-orange-50 border-l-4 border-[#F46A25] rounded-sm p-4 text-sm text-[#12263A] leading-relaxed max-w-4xl"
          data-testid="category-city-intro"
        >
          <p>
            Looking for <b>{category.name.toLowerCase()}</b> in <b>{cityMatch.name}</b>?
            Dr Tools UK offers fast delivery on all professional {category.name.toLowerCase()} supplies to the {cityMatch.name} area,
            with next-day dispatch on orders placed before 2 PM. Whether you are a working builder,
            a local retailer stocking your shelves, or a homeowner tackling a weekend project, our {cityMatch.name} customers
            get the same trade pricing, open credit accounts and free delivery on orders over £75 as our
            nationwide network. Every product in our <b>{category.name}</b> range ships from a UK warehouse and comes
            with a 90-day returns policy, so buying {category.name.toLowerCase()} online in {cityMatch.name} has never been simpler.
          </p>
        </div>
      )}

      {/* SEO intro copy (100-150 words) — editable by admin per category */}
      {!isSale && !cityMatch && category.seo_intro && (
        <div className="mb-6 bg-white border border-neutral-200 rounded-sm p-4 text-sm text-neutral-700 leading-relaxed max-w-4xl" data-testid="category-seo-intro">
          {category.seo_intro}
        </div>
      )}
      {isSale && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-600 rounded-sm p-4 text-sm text-red-900 leading-relaxed max-w-4xl" data-testid="sale-banner">
          <b>Everything on sale, all in one place.</b> Every discounted product across Dr Tools is auto-listed here — from tool clearance to seasonal offers. Prices update live; no promo code needed.
        </div>
      )}

      {subCategories.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-bold text-[#12263A] mb-3 uppercase">Shop by sub-category</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {subCategories.map(sc => (
              <Link key={sc.id} to={cityMatch ? `/category/${sc.slug}-in-${cityMatch.slug}` : `/category/${sc.slug}`} className="group bg-white border border-neutral-200 hover:border-[#F46A25] rounded-sm overflow-hidden transition-all">
                <div className="aspect-square overflow-hidden bg-neutral-50">
                  <img src={resolveImage(sc.image)} alt={sc.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-2 text-center text-xs font-semibold text-[#12263A] group-hover:text-[#F46A25] line-clamp-2 min-h-[2.5rem]">{sc.name}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Mobile: filter opener */}
      <div className="lg:hidden mb-3">
        <button onClick={() => setShowFilters(true)} data-testid="mobile-filter-open" className="w-full flex items-center justify-center gap-2 bg-white border border-neutral-300 rounded-sm py-2 text-sm font-bold text-[#12263A]">
          <SlidersHorizontal className="w-4 h-4" /> Filters {activeFilterCount > 0 && <span className="bg-[#F46A25] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">{activeFilterCount}</span>}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar (desktop always visible, mobile modal) */}
        <aside
          data-testid="filter-sidebar"
          className={`bg-white border border-neutral-200 rounded-sm p-4 h-fit ${showFilters ? 'fixed inset-4 z-50 overflow-y-auto max-h-[92vh]' : 'hidden lg:block'}`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-[#12263A]" />
              <h3 className="font-bold text-[#12263A]">Filter</h3>
              {activeFilterCount > 0 && <span className="bg-[#F46A25] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">{activeFilterCount}</span>}
            </div>
            <div className="flex items-center gap-2">
              {activeFilterCount > 0 && (
                <button onClick={clearAll} data-testid="filter-clear" className="text-[11px] text-[#F46A25] hover:underline font-bold">Clear all</button>
              )}
              <button onClick={() => setShowFilters(false)} className="lg:hidden text-neutral-500"><X className="w-5 h-5" /></button>
            </div>
          </div>

          {/* Price */}
          <div className="mb-6" data-testid="filter-price">
            <div className="text-sm font-semibold text-[#12263A] mb-2">Price range</div>
            <div className="flex items-center gap-2">
              <input type="number" min="0" value={minPrice} onChange={e => setMinPrice(Math.max(0, Number(e.target.value) || 0))} className="w-full border border-neutral-300 rounded-sm px-2 py-1 text-xs" data-testid="filter-min-price" />
              <span className="text-xs text-neutral-500">to</span>
              <input type="number" min="0" value={maxPrice} onChange={e => setMaxPrice(Math.max(0, Number(e.target.value) || 0))} className="w-full border border-neutral-300 rounded-sm px-2 py-1 text-xs" data-testid="filter-max-price" />
            </div>
            <input type="range" min="0" max={priceCap} step="10" value={maxPrice} onChange={e => setMaxPrice(+e.target.value)} className="w-full accent-[#F46A25] mt-2" />
            <div className="text-[10px] text-neutral-500">£{minPrice} — £{maxPrice}</div>
          </div>

          {/* Availability */}
          <div className="mb-6" data-testid="filter-availability">
            <div className="text-sm font-semibold text-[#12263A] mb-2">Availability</div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={inStockOnly} onChange={() => setInStockOnly(v => !v)} className="accent-[#F46A25]" data-testid="filter-in-stock" />
              <span>In stock only</span>
            </label>
          </div>

          {/* Brand */}
          {availableBrands.length > 0 && (
            <div className="mb-6" data-testid="filter-brand">
              <div className="text-sm font-semibold text-[#12263A] mb-2">Brand</div>
              <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                {availableBrands.map(b => (
                  <label key={b} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={selBrands.includes(b)} onChange={() => toggleBrand(b)} className="accent-[#F46A25]" data-testid={`filter-brand-${b}`} />
                    <span>{b}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Dynamic attribute groups (voltage, power source, etc.) */}
          {Object.entries(availableAttrs).map(([key, values]) => (
            <div key={key} className="mb-6" data-testid={`filter-attr-${key}`}>
              <div className="text-sm font-semibold text-[#12263A] mb-2 capitalize">
                {key.replace(/_/g, ' ')}
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                {values.map(v => (
                  <label key={v} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(selAttrs[key] || []).includes(v)}
                      onChange={() => toggleAttr(key, v)}
                      className="accent-[#F46A25]"
                      data-testid={`filter-attr-${key}-${v}`}
                    />
                    <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}

          {showFilters && (
            <div className="pt-2 border-t border-neutral-200 lg:hidden">
              <button onClick={() => setShowFilters(false)} data-testid="mobile-filter-apply" className="w-full bg-[#F46A25] hover:bg-[#e05a15] text-white text-sm font-bold py-2 rounded-sm">
                Show {filtered.length} products
              </button>
            </div>
          )}
        </aside>

        <div>
          <div className="flex items-center justify-between mb-4 bg-white border border-neutral-200 rounded-sm px-4 py-2">
            <span className="text-sm text-neutral-600">Showing {filtered.length} results</span>
            <select value={sort} onChange={e => setSort(e.target.value)} className="border border-neutral-300 rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#F46A25]" data-testid="sort-select">
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="rating">Best rated</option>
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-neutral-200 rounded-sm p-10 text-center">
              <p className="text-neutral-600">{products.length === 0 ? (isSale ? 'No sale items right now — check back soon.' : 'No products in this category yet.') : 'No products match these filters. Try clearing some.'}</p>
              {products.length > 0 && (
                <button onClick={clearAll} className="mt-3 text-sm font-bold text-[#F46A25] hover:underline">Clear filters</button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {filtered.map(p => <ProductCard key={`${p.id}-${p.variant_id || 'base'}`} product={p} isOnSale={isOnSale(p)} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
